import { useCallback } from "react";

import { format } from "date-fns";
import { formatInTimeZone, zonedTimeToUtc } from "date-fns-tz";

import { useAppSelector } from "app/hooks";
import { selectCurrentPractice } from "features/auth/auth-slice";
import { useValidateRoomAvailabilityMutation } from "services/appointments";
import { Clinic } from "services/types/clinics.type";
import { Patient } from "services/types/patients.type";
import { Room } from "services/types/rooms.type";

export default function useRoomValidation() {
  const [validateRoomAvailability] = useValidateRoomAvailabilityMutation();
  const currentPractice = useAppSelector(selectCurrentPractice);

  const isRoomAvailable = useCallback(
    async (
      room: Room,
      appointmentDate: Date,
      startTime: Date,
      endTime: Date,
      title: string,
      patient: Patient,
      clinic: Clinic,
      appointmentId?: number,
      roomStartMoment?: Date | null,
      roomEndMoment?: Date | null
    ) => {
      if (currentPractice?.scheduling_ignore_room_reservation) {
        return true;
      }
      const startMoment = new Date(appointmentDate);
      startMoment.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);

      // A time that lands before the appointment start belongs to the NEXT day — the window
      // crosses midnight. Roll it forward so the room check runs on a valid chronological
      // window instead of an inverted one (which the backend reads as a false conflict).
      const onApptTimeline = (d: Date) => {
        if (d < startMoment) d.setDate(d.getDate() + 1);
        return d;
      };

      const endMoment = new Date(appointmentDate);
      endMoment.setHours(endTime.getHours(), endTime.getMinutes(), 0, 0);
      onApptTimeline(endMoment);

      const formatMoment = (date: Date) =>
        formatInTimeZone(
          format(
            clinic?.timezone ? zonedTimeToUtc(date, clinic.timezone) : date,
            "yyyy-MM-dd HH:mm:ssXXX"
          ),
          clinic?.timezone || "UTC",
          "yyyy-MM-dd HH:mm:ssXXX"
        );

      const buildRoomMoment = (timePicker: Date): Date => {
        const d = new Date(appointmentDate);
        d.setHours(timePicker.getHours(), timePicker.getMinutes(), 0, 0);
        return onApptTimeline(d);
      };

      try {
        const result = await validateRoomAvailability({
          room: room.id.toString(),
          start_moment: formatMoment(startMoment),
          end_moment: formatMoment(endMoment),
          validation_mode: "room",
          title: title || "Appointment",
          patient: patient?.id,
          clinic: clinic.id,
          ...(appointmentId && { current_appointment_id: appointmentId }),
          ...(roomStartMoment && {
            room_start_moment: formatMoment(buildRoomMoment(roomStartMoment))
          }),
          ...(roomEndMoment && {
            room_end_moment: formatMoment(buildRoomMoment(roomEndMoment))
          })
        });

        if ("error" in result) {
          return false;
        }
        return result.data.isValid;
      } catch {
        return false;
      }
    },
    [
      validateRoomAvailability,
      currentPractice?.scheduling_ignore_room_reservation
    ]
  );

  return {
    isRoomAvailable
  };
}
